import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Table,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Icon } from "../utils/iconHelper";
import StudyResourceService, { StudyResource } from "../_services/StudyResourceService";
import "./ResourcePoolBrowser.css";

interface ResourcePoolBrowserProps {
  show: boolean;
  onHide: () => void;
  onSelectResource: (resource: StudyResource) => void;
  onSelectMultiple?: (resources: StudyResource[]) => void;
  multiSelect?: boolean;
  filterByType?: number; // Optional: pre-filter by resource type
}

const ResourcePoolBrowser: React.FC<ResourcePoolBrowserProps> = ({
  show,
  onHide,
  onSelectResource,
  onSelectMultiple,
  multiSelect = false,
  filterByType,
}) => {
  const [resources, setResources] = useState<StudyResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<number | undefined>(filterByType);
  const [selectedResources, setSelectedResources] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (show) {
      fetchResources();
    }
  }, [show, typeFilter]);

  const fetchResources = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await StudyResourceService.getStudyResources({
        typeId: typeFilter,
        search: searchTerm || undefined,
        limit: 100,
      });
      setResources(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchResources();
  };

  const handleSelect = (resource: StudyResource) => {
    if (multiSelect) {
      const newSelected = new Set(selectedResources);
      if (newSelected.has(resource.id!)) {
        newSelected.delete(resource.id!);
      } else {
        newSelected.add(resource.id!);
      }
      setSelectedResources(newSelected);
    } else {
      onSelectResource(resource);
      onHide();
    }
  };

  const handleAddSelected = () => {
    if (multiSelect && onSelectMultiple) {
      const selected = resources.filter((r) => selectedResources.has(r.id!));
      onSelectMultiple(selected);
      setSelectedResources(new Set());
      onHide();
    }
  };

  const getTypeIcon = (typeId: number) => {
    const iconProps = { className: "me-2", size: 16 };
    switch (typeId) {
      case 1: return <Icon.Download {...iconProps} />;
      case 5: return <Icon.Link {...iconProps} />;
      case 6: return <Icon.FileText {...iconProps} />;
      case 13: return <Icon.Book {...iconProps} />;
      case 27: return <Icon.Package {...iconProps} />;
      case 31:
      case 32: return <Icon.Video {...iconProps} />;
      default: return null;
    }
  };

  const getTypeBadgeColor = (typeId: number) => {
    switch (typeId) {
      case 1: return "primary";
      case 5: return "info";
      case 6: return "success";
      case 13: return "dark";
      case 27: return "primary";
      case 31: return "warning";
      case 32: return "danger";
      default: return "secondary";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="resource-pool-browser"
      enforceFocus={false}
      backdrop="static"
      onEscapeKeyDown={(e) => {
        e.stopPropagation();
        onHide();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <Icon.Folder className="me-2" />
          Resource Pool
        </Modal.Title>
      </Modal.Header>

      <Modal.Body onClick={(e) => e.stopPropagation()}>
        {/* Search and Filter */}
        <div className="mb-4" style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
          <InputGroup onClick={(e) => e.stopPropagation()} size="lg" style={{ flex: 1 }}>
            <InputGroup.Text>
              <Icon.Search size={18} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              onKeyPress={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") handleSearch();
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <Button variant="primary" onClick={(e) => {
              e.stopPropagation();
              handleSearch();
            }}>
              Search
            </Button>
          </InputGroup>
          <Form.Select
            value={typeFilter || ""}
            onChange={(e) => {
              e.stopPropagation();
              setTypeFilter(e.target.value ? Number(e.target.value) : undefined);
            }}
            onClick={(e) => e.stopPropagation()}
            size="lg"
            style={{ width: 'auto', minWidth: '150px', flex: 'none' }}
          >
            <option value="">All Types</option>
            <option value="6">Article</option>
            <option value="1">Download</option>
            <option value="13">E-Book</option>
            <option value="5">Reference</option>
            <option value="27">SCORM Course</option>
            <option value="31">Vimeo Video</option>
            <option value="32">YouTube Video</option>
          </Form.Select>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner role="status" animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading resources...</p>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-5">
            <Icon.Inbox size={48} className="text-muted mb-3" />
            <h5 className="text-muted">No resources found</h5>
            <p className="text-muted">
              {searchTerm || typeFilter
                ? "Try adjusting your search or filter"
                : "Create resources to build your library"}
            </p>
          </div>
        ) : (
          <div className="resource-pool-table">
            <Table hover responsive>
              <thead>
                <tr>
                  {multiSelect && <th style={{ width: "50px" }}></th>}
                  <th>Resource Name</th>
                  <th style={{ width: "150px" }}>Created</th>
                  <th style={{ width: "130px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((resource) => (
                  <tr
                    key={resource.id}
                    className={
                      multiSelect && selectedResources.has(resource.id!)
                        ? "table-active"
                        : ""
                    }
                    onClick={multiSelect ? (e) => {
                      e.stopPropagation();
                      handleSelect(resource);
                    } : (e) => e.stopPropagation()}
                    style={{ cursor: multiSelect ? "pointer" : "default" }}
                  >
                    {multiSelect && (
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedResources.has(resource.id!)}
                          onChange={() => handleSelect(resource)}
                        />
                      </td>
                    )}
                    <td>
                      <div className="d-flex align-items-center">
                        {getTypeIcon(resource.typeId)}
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="fw-semibold">{resource.name}</span>
                            <Badge bg={getTypeBadgeColor(resource.typeId)} className="resource-type-badge">
                              {resource.typeName ||
                                StudyResourceService.getResourceTypeName(resource.typeId)}
                            </Badge>
                          </div>
                          {resource.url && (
                            <small
                              className="text-muted text-truncate d-block"
                              style={{ maxWidth: "600px" }}
                            >
                              {resource.url}
                            </small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-muted">
                      {formatDate(resource.creationDate)}
                    </td>
                    <td>
                      {!multiSelect && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(resource);
                          }}
                        >
                          <Icon.Plus className="me-1" />
                          Add
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {multiSelect && selectedResources.size > 0 && (
              <div className="resource-pool-footer">
                <div className="text-muted">
                  <strong>{selectedResources.size}</strong> resource(s) selected
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            <strong>{resources.length}</strong> resources available
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Cancel
            </Button>
            {multiSelect && (
              <Button
                variant="primary"
                onClick={handleAddSelected}
                disabled={selectedResources.size === 0}
              >
                <Icon.Plus className="me-2" />
                Add Selected ({selectedResources.size})
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ResourcePoolBrowser;
